import { Box, MenuList, Portal, StyleProps } from '@chakra-ui/react';
import { FC, PropsWithChildren, useCallback, useEffect, useRef } from 'react';

import { useMenuSize } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { useIntersectionObserver } from '../../../hooks/use-intersection-observer';
import { useDropdownContext } from '../lib/context';

export interface DropdownMenuProps extends StyleProps, PropsWithChildren {
	// scroll to this child index when the menu opens (if no value is selected)
	defaultScrollIndex?: number;
}

export const DropdownMenu: FC<DropdownMenuProps> = ({ children, defaultScrollIndex, ...rest }) => {
	const { isOpen, onScrollToBottom, value } = useDropdownContext();
	const ref = useMenuSize();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const hasScrolledRef = useRef(false);

	const enableObserver = !!onScrollToBottom;

	const { rootRef, targetRef } = useIntersectionObserver({
		enabled: enableObserver,
		onEnterIntersection: onScrollToBottom,
	});

	// scroll to default position when menu opens (only if no value is selected)
	useEffect(() => {
		if (isOpen && defaultScrollIndex && !value && !hasScrolledRef.current) {
			hasScrolledRef.current = true;
			// defer scroll to next frame after menu renders
			requestAnimationFrame(() => {
				const container = scrollContainerRef.current;
				if (!container) return;

				const items = container.querySelectorAll('[role="menuitem"]');
				const targetItem = items[defaultScrollIndex] as HTMLElement;
				if (targetItem) {
					targetItem.scrollIntoView({ block: 'center' });
				}
			});
		}

		if (!isOpen) {
			hasScrolledRef.current = false;
		}
	}, [isOpen, defaultScrollIndex, value]);

	const setScrollContainerRef = useCallback((el: HTMLDivElement | null) => {
		(scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
	}, []);

	return (
		<Portal>
			<MenuList
				ref={(el) => {
					ref(el);
					rootRef(el);
				}}
				zIndex="dropdown"
				{...rest}
			>
				<Box ref={setScrollContainerRef} maxH={rem(360)} overflowY="auto">
					{children}
					{!!enableObserver && <div ref={targetRef} style={{ height: rem(1) }} />}
				</Box>
			</MenuList>
		</Portal>
	);
};
